import { memo, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Button } from 'baseui/button'
import { Check, Overflow } from 'baseui/icon'
import { Modal } from 'baseui/modal'
import { PLACEMENT, StatefulPopover, TRIGGER_TYPE } from 'baseui/popover'
import AbandonModalContent from '@/shared/components/contracts/AbandonModalContent'
import IconCustomZoho from '@/shared/icons/IconCustomZoho'
import ActionsMenuContent from './ActionsMenuContent'
import { ActionButtonOverrides, ActionsMoreButtonOverrides, ActionsRow } from './styled-components'

const buildPinnedItems = (row, t) => [
  row.hasContract && {
    key: 'signNet',
    label: t('contacts.actions.sign_to_net'),
    icon: Check,
    isLoading: row.isSignNetLoading,
    disabled: row.signNetDisabled,
    title: row.signNetDisabled ? t('contacts.actions.sign_to_net_disabled') : undefined,
    onClick: row.onSignNet,
  },
  row.hasContract && {
    key: 'resyncZoho',
    label: t('contacts.actions.resync_zoho'),
    icon: IconCustomZoho,
    isLoading: row.isResyncZohoLoading,
    disabled: row.resyncZohoDisabled,
    title: row.resyncZohoDisabled ? t('contacts.actions.resync_zoho_disabled') : undefined,
    onClick: row.onResyncZoho,
  },
].filter(Boolean)

const ActionRowComponent = ({ row, t }) => {
  const [abandonModalOpen, setAbandonModalOpen] = useState(false)

  const handleAbandonSubmit = useCallback((reason, callback) => {
    if (!row.onSignAbandon) return
    row.onSignAbandon(reason, (success) => {
      callback(success)
      if (success) {
        setAbandonModalOpen(false)
      }
    })
  }, [row.onSignAbandon])

  const openAbandonModal = useCallback(() => setAbandonModalOpen(true), [])

  const pinnedItems = buildPinnedItems(row, t)

  return (
    <>
      <ActionsRow>
        {pinnedItems.map(item => (
          <Button
            key={item.key}
            size='compact'
            kind='secondary'
            isLoading={item.isLoading}
            disabled={item.disabled}
            title={item.title}
            onClick={item.onClick}
            overrides={ActionButtonOverrides}
            startEnhancer={item.icon ? () => <item.icon size={16} /> : undefined}
          >
            {item.label}
          </Button>
        ))}

        <StatefulPopover
          placement={PLACEMENT.bottomRight}
          triggerType={TRIGGER_TYPE.click}
          accessibilityType='menu'
          content={({ close }) => (
            <ActionsMenuContent row={row} t={t} close={close} onOpenAbandonModal={openAbandonModal} />
          )}
        >
          <Button
            size='compact'
            kind='secondary'
            title={t('contacts.actions.more_actions_hint')}
            overrides={ActionsMoreButtonOverrides}
            startEnhancer={() => <Overflow size={16} />}
          >
            {t('contacts.actions.more_actions')}
          </Button>
        </StatefulPopover>
      </ActionsRow>

      <Modal isOpen={abandonModalOpen} onClose={() => setAbandonModalOpen(false)}>
        <AbandonModalContent onSubmit={handleAbandonSubmit} minLength={10} />
      </Modal>
    </>
  )
}

ActionRowComponent.propTypes = {
  row: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
}

export default memo(ActionRowComponent)
